import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Store } from '@ngrx/store';
import { first } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslocoService } from '@jsverse/transloco';
import { PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { IFilter, IGridState, PageSize } from 'src/app/core/models/base';
import { IAssignment } from 'src/app/assignment/models/assignment';
import { IEmployee } from 'src/app/employee/employee';
import { downloadBlob } from 'src/app/shared/helpers/fileHelper';
import { toDayBoundaryIso } from 'src/app/shared/helpers/date-helpers';
import { AppStateInterface } from 'src/app/stores/global/appState.interface';
import { currentEmployeeSelector } from 'src/app/stores/global/current-employee/current-employee.store';
import { ExpenseService } from '../../services/expense.service';
import { IAssignmentOption } from '../expense-dialog/expense-dialog.component';
import {
  EMPTY_EXPENSE_GRID_RESULT,
  ExpenseStatus,
  ExportFormat,
  IExpense,
  IExpenseGridResult,
  IExportExpensesRequest,
  ISearchExpensesRequest,
  expenseSortToOrderBy,
  sumExpenseAmounts,
  toExpenseGridResult
} from '../../models/expense';

// "Spesen" tab of an assignment: every expense linked to it that the current user may see (own +
// subordinates, or every employee for HR/accounting), with employee/status/date filter, paging, sort,
// Excel/PDF export, totals and "create" with the assignment preselected.
@UntilDestroy()
@Component({
    selector: 'app-expense-assignment-list',
    templateUrl: './expense-assignment-list.component.html',
    standalone: false
})
export class ExpenseAssignmentListComponent implements OnInit, OnChanges {

  @Input() assignment: IAssignment | null = null;

  expenses: IExpenseGridResult = EMPTY_EXPENSE_GRID_RESULT;
  selectedIds: string[] = [];

  // Amount sum of the displayed page; the grand total of the filtered set comes from the backend.
  get pageAmount(): number {
    return sumExpenseAmounts(this.expenses.data);
  }

  gridState: IGridState = { pageSize: 25, skip: 0 };

  sort: SortDescriptor[] = [{ field: 'date', dir: 'desc' }];

  // The (paged) request behind the displayed list - reused for reloads and, widened, for exports.
  private lastRequest: ISearchExpensesRequest | null = null;

  get pageSizes() {
    return PageSize;
  }

  // Dropdown items (Excel / PDF) for both export buttons.
  exportFormatItems = [
    { text: 'Excel', format: ExportFormat.Excel },
    { text: 'PDF', format: ExportFormat.Pdf }
  ];

  // dialog state
  dialogOpen = false;
  dialogId: string | null = null;
  dialogEditable = false;

  private currentEmployeeId: string | null = null;

  ExpenseStatus = ExpenseStatus;

  // --- filter (employee, status, expense-date range), like the "all expenses" list ---

  // Only the employees the current user is authorized to control (subordinates + self, or all for HR).
  employees: IEmployee[] = [];

  // Every status is selectable here; null = all.
  private readonly allStatuses = [
    ExpenseStatus.Open,
    ExpenseStatus.Submitted,
    ExpenseStatus.Accepted,
    ExpenseStatus.Rejected,
    ExpenseStatus.Paid
  ];

  // Primitive values with a translated text (Kendo requires textField and valueField together).
  statusFilterItems: { value: ExpenseStatus; text: string }[] = [];
  allStatusesItem: { value: ExpenseStatus | null; text: string } = { value: null, text: '' };

  filterForm = this.fb.group({
    employee: [null],
    status: [null],
    dateRangeStart: [null],
    dateRangeEnd: [null]
  });

  constructor(
    private expenseService: ExpenseService,
    private fb: UntypedFormBuilder,
    private translocoService: TranslocoService,
    private store: Store<AppStateInterface>
  ) {
    this.store.select(currentEmployeeSelector)
      .pipe(untilDestroyed(this))
      .subscribe(employee => this.currentEmployeeId = employee?.id ?? null);
  }

  ngOnInit(): void {
    this.statusFilterItems = this.allStatuses.map(value => ({
      value,
      text: this.translocoService.translate('expenses.expenseStates.' + ExpenseStatus[value])
    }));
    this.allStatusesItem = { value: null, text: this.translocoService.translate('expenses.filterAllStatuses') };

    this.expenseService.getReviewableEmployees()
      .pipe(first(), untilDestroyed(this))
      .subscribe(employees => this.employees = employees);
  }

  // The assignment arrives asynchronously from the store (null until resolved).
  ngOnChanges(changes: SimpleChanges): void {
    const previous: IAssignment | null = changes['assignment']?.previousValue ?? null;
    if (this.assignment?.id && this.assignment.id !== previous?.id) {
      this.onSubmitFilter();
    }
  }

  onSubmitFilter(): void {
    // A new filter always restarts at the first page.
    this.gridState.skip = 0;
    this.loadPage(1, this.gridState.pageSize);
  }

  onResetFilter(): void {
    this.filterForm.reset();
    this.onSubmitFilter();
  }

  // The create dialog preselects this assignment in its combobox.
  get presetAssignment(): IAssignmentOption | null {
    return this.assignment ? { id: this.assignment.id, title: this.assignment.title } : null;
  }

  private loadPage(pageNumber: number, pageSize: number): void {
    this.selectedIds = [];
    this.lastRequest = this.buildRequest(pageNumber, pageSize);
    this.load(this.lastRequest);
  }

  // --- paging (server-side) ---
  onPageChange({ skip, take }: PageChangeEvent): void {
    this.gridState.skip = skip;
    this.gridState.pageSize = take;
    this.loadPage(Math.round((skip + take) / take), take);
  }

  onPageSizeChange(pageSize: number): void {
    this.onPageChange({ skip: this.gridState.skip, take: pageSize });
  }

  onGridReload(): void {
    if (this.lastRequest) {
      this.load(this.lastRequest);
    }
  }

  // --- sorting (server-side) ---
  onSortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.gridState.skip = 0;
    this.loadPage(1, this.gridState.pageSize);
  }

  private buildRequest(pageNumber: number, pageSize: number): ISearchExpensesRequest {
    const formValues = this.filterForm.value;
    const status: ExpenseStatus | null = formValues.status ?? null;

    // Filter via the non-nullable Assignment.Id navigation key (the nullable FK column is not
    // handled by the backend's generic filter value converter).
    const filters: IFilter[] = [
      { field: 'LinkedAssignment.Id', operator: 'eq', value: this.assignment?.id }
    ];

    if (formValues.employee?.id) {
      filters.push({ field: 'SubmitterId', operator: 'eq', value: formValues.employee.id });
    }

    if (formValues.dateRangeStart) {
      filters.push({ field: 'Date', operator: 'gte', value: toDayBoundaryIso(formValues.dateRangeStart, false) });
    }

    if (formValues.dateRangeEnd) {
      filters.push({ field: 'Date', operator: 'lte', value: toDayBoundaryIso(formValues.dateRangeEnd, true) });
    }

    return {
      pageNumber,
      pageSize,
      orderBy: expenseSortToOrderBy(this.sort),
      // Control scope including own expenses: the widest read reach the user has.
      forApproval: true,
      includeOwn: true,
      statuses: status == null ? [] : [status],
      advancedFilter: filters.length === 1 ? filters[0] : { logic: 'and', filters }
    };
  }

  private load(request: ISearchExpensesRequest): void {
    this.expenseService.searchExpensesForApproval(request)
      .pipe(first(), untilDestroyed(this))
      .subscribe(response => this.expenses = toExpenseGridResult(response));
  }

  // --- export (selection or whole list) ---
  exportAll(item: { format: ExportFormat }): void {
    this.export(null, item.format);
  }

  exportSelected(item: { format: ExportFormat }): void {
    if (!this.selectedIds.length) {
      return;
    }
    this.export(this.selectedIds, item.format);
  }

  private export(selectedExpenseIds: string[] | null, format: ExportFormat): void {
    const request: IExportExpensesRequest = {
      ...(this.lastRequest ?? this.buildRequest(1, this.gridState.pageSize)),
      // Export the whole list, not just the current page.
      pageNumber: 1,
      pageSize: 999999,
      selectedExpenseIds: selectedExpenseIds ?? undefined,
      format
    };

    const fileName = format === ExportFormat.Pdf ? 'Spesen.pdf' : 'Spesen.xlsx';

    this.expenseService.exportExpenses(request)
      .pipe(first(), untilDestroyed(this))
      .subscribe(response => downloadBlob(response, fileName));
  }

  // --- view / create ---
  statusName(status: ExpenseStatus): string {
    return ExpenseStatus[status];
  }

  statusClass(status: ExpenseStatus): string {
    return 'status-' + ExpenseStatus[status].toLowerCase();
  }

  openCreate(): void {
    this.dialogId = null;
    this.dialogEditable = true;
    this.dialogOpen = true;
  }

  // An own Open or Rejected expense can still be edited (and submitted) from here.
  open(expense: IExpense): void {
    this.dialogId = expense.id;
    this.dialogEditable = expense.submitterId === this.currentEmployeeId
      && (expense.status === ExpenseStatus.Open || expense.status === ExpenseStatus.Rejected);
    this.dialogOpen = true;
  }

  closeDialog(): void {
    this.dialogOpen = false;
    this.dialogId = null;
    this.dialogEditable = false;
  }
}
